import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  CopyButton,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconCheck, IconChevronDown, IconChevronUp, IconCopy, IconPencil, IconPlus } from '@tabler/icons-react';
import {
  DEFAULT_PRICE_GRACE_DAYS,
  SELLABLE_CONTENT_KINDS,
  SELLABLE_CONTENT_LABELS,
  SUBSCRIPTION_PLAN_LABELS,
  buyPath,
  formatPlanDuration,
  formatRubles,
  type AdminProductRow,
  type SellableContentKind,
  type PaymentMode,
  type PaymentsStatusView,
  type SubscriptionPlan,
} from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { ADMIN_CONTENT_KEY, fetchAdminContent } from './api';
import {
  ADMIN_PAYMENTS_STATUS_KEY,
  ADMIN_PRODUCTS_KEY,
  OFFER_KEY,
  changeProductPrice,
  createProduct,
  fetchPaymentsStatus,
  fetchProducts,
  savePaymentsMode,
  saveRecurringSetting,
  updateProduct,
} from './payments-api';

/**
 * Тарифы и оплата (docs/PAYMENTS.md, §4.3, §10).
 *
 * Товары — то, что продаётся на платформе через Robokassa, под своим кодом. При смене цены прежняя ещё несколько дней принимается, чтобы
 * заказы, оформленные до смены, не уходили на разбор. Режим приёма оплат
 * переключается здесь же; боевой — только когда на сервере заданы пароли и
 * почта. В боевом режиме тестовые пометки и настройки не показываются.
 */

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export function AdminProductsPage() {
  const status = useQuery({ queryKey: ADMIN_PAYMENTS_STATUS_KEY, queryFn: fetchPaymentsStatus });
  const products = useQuery({ queryKey: ADMIN_PRODUCTS_KEY, queryFn: fetchProducts });
  const [showDisabled, setShowDisabled] = useState(false);
  const [adding, setAdding] = useState(false);
  const active = products.data?.items.filter((product) => product.active) ?? [];
  const disabled = products.data?.items.filter((product) => !product.active) ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Тарифы и оплата"
        description="Тарифы, материалы и игры, которые продаются на платформе, их цены и платёжные ссылки. Состав тарифов — флажками «Тариф Лайт» и «Тариф Про» у материалов."
      />
      {status.isLoading || products.isLoading ? (
        <LoadingState label="Загружаем..." />
      ) : status.isError || products.isError || !status.data || !products.data ? (
        <ErrorState onRetry={() => { void status.refetch(); void products.refetch(); }} />
      ) : (
        <Stack gap="lg">
          <PaymentModeForm status={status.data} />
          <RecurringForm status={status.data} />

          <Paper withBorder p="md" radius="lg">
            <Stack gap="sm">
              <Group justify="space-between">
                <Title order={5}>Товары</Title>
                {adding ? null : (
                  <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>Новый товар</Button>
                )}
              </Group>
              <Text size="xs" c="var(--text-muted)">
                Платёжную ссылку товара можно поставить на сайт, в Telegram или в письмо: по ней человек входит на платформу
                и сразу попадает на оплату.
              </Text>
              {adding ? <NewProductForm onClose={() => setAdding(false)} /> : null}
              {active.length === 0 ? <Text size="sm" c="var(--text-muted)">Сейчас ничего не продаётся.</Text> : null}
              {PRODUCT_GROUPS.map((group) => {
                const rows = active.filter((product) => product.grantType === group.grantType);
                return rows.length === 0 ? null : (
                  <Stack key={group.grantType} gap="xs">
                    <Text size="xs" fw={600} tt="uppercase" c="var(--text-muted)">{group.label}</Text>
                    {rows.map((product) => <ProductRow key={product.id} product={product} />)}
                  </Stack>
                );
              })}
              {disabled.length > 0 ? (
                <>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    w="fit-content"
                    leftSection={showDisabled ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                    onClick={() => setShowDisabled((value) => !value)}
                  >
                    Выключенные · {disabled.length}
                  </Button>
                  {showDisabled ? disabled.map((product) => <ProductRow key={product.id} product={product} />) : null}
                </>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      )}
    </>
  );
}

/**
 * Режим приёма оплат. Тестовый — заказы записываются, но доступа не выдают;
 * боевой — настоящие продажи. Сервер не включит боевой, пока чего-то не
 * хватает (`liveProblems`): тогда вариант недоступен и видно, чего именно.
 */
function PaymentModeForm({ status }: { status: PaymentsStatusView }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (mode: PaymentMode) => savePaymentsMode({ mode }),
    onSuccess: (result) => {
      queryClient.setQueryData(ADMIN_PAYMENTS_STATUS_KEY, result);
      void queryClient.invalidateQueries({ queryKey: OFFER_KEY });
      notifications.show({ message: result.live ? 'Включён боевой режим' : 'Включён тестовый режим', color: 'teal' });
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });
  const change = (mode: PaymentMode) =>
    modals.openConfirmModal({
      title: mode === 'live' ? 'Включить боевой режим?' : 'Вернуть тестовый режим?',
      children: (
        <Text size="sm" c="var(--text-muted)">
          {mode === 'live'
            ? 'Оплаты станут настоящими: Robokassa спишет деньги, покупатель получит доступ, письмо и чек.'
            : 'Новые счета станут тестовыми: деньги не списываются и доступ не выдаётся. Уже выставленные боевые счета при оплате обработаются как боевые.'}
        </Text>
      ),
      labels: { confirm: mode === 'live' ? 'Включить боевой' : 'Вернуть тестовый', cancel: 'Отмена' },
      onConfirm: () => mutation.mutate(mode),
    });
  const blocked = status.liveProblems.length > 0;
  return (
    <Paper withBorder p="md" radius="lg" data-testid="payments-mode">
      <Stack gap="xs">
        <Group gap="xs">
          <Title order={5}>Приём оплат</Title>
          <Badge color={status.live ? 'teal' : 'grape'} variant="light">
            {status.live ? 'Боевой режим' : 'Тестовый режим'}
          </Badge>
        </Group>
        <SegmentedControl
          size="xs"
          w={320}
          value={status.live ? 'live' : 'test'}
          disabled={mutation.isPending}
          onChange={(value) => change(value as PaymentMode)}
          data={[
            { value: 'test', label: 'Тестовый' },
            { value: 'live', label: 'Боевой', disabled: !status.live && blocked },
          ]}
        />
        {!status.live && blocked ? (
          <Stack gap={2}>
            <Text size="xs" c="var(--warning-text, orange)">Боевой режим пока не включить:</Text>
            {status.liveProblems.map((problem) => (
              <Text key={problem} size="xs" c="var(--warning-text, orange)">— {problem}</Text>
            ))}
          </Stack>
        ) : null}
        {!status.directActive && !(status.live && blocked) ? (
          <Alert variant="light" color="red" p="xs">
            <Text size="xs">
              Оплата сейчас не работает: для {status.live ? 'боевого' : 'тестового'} режима на сервере не заданы пароли Robokassa.
            </Text>
          </Alert>
        ) : null}
        {status.live && blocked ? (
          <Alert variant="light" color="red" p="xs">
            <Text size="xs">Боевой режим включён, но на сервере не хватает настроек: {status.liveProblems.join(' ')}</Text>
          </Alert>
        ) : null}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
          <Text size="sm">Пароли Robokassa, боевые: {status.directLiveConfigured ? 'заданы' : 'нет'}</Text>
          {status.live ? null : (
            <Text size="sm">Пароли Robokassa, тестовые: {status.directTestConfigured ? 'заданы' : 'нет'}</Text>
          )}
          <Text size="sm">Проверка оплат в Robokassa: {status.robokassaVerification ? 'настроена' : 'не настроена'}</Text>
          <Text size="sm">Письма о заказах на разбор: {status.alertEmail}</Text>
        </SimpleGrid>
      </Stack>
    </Paper>
  );
}

/** Порядок групп в списке продающихся товаров. */
const PRODUCT_GROUPS: { grantType: AdminProductRow['grantType']; label: string }[] = [
  { grantType: 'plan', label: 'Тарифы' },
  { grantType: 'content_access', label: 'Материалы' },
  { grantType: 'game_ownership', label: 'Игры' },
];

/**
 * Автопродление (docs/payments/RECURRING.md): предлагать ли покупателям
 * галочку согласия у тарифов на 30 дней. Включать — когда Robokassa подключила
 * услугу магазину. Выключение не трогает уже подключённые автопродления.
 */
function RecurringForm({ status }: { status: PaymentsStatusView }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (enabled: boolean) => saveRecurringSetting({ enabled }),
    onSuccess: (result) => {
      queryClient.setQueryData(ADMIN_PAYMENTS_STATUS_KEY, result);
      notifications.show({ message: result.recurringEnabled ? 'Автопродление предлагается покупателям' : 'Автопродление больше не предлагается', color: 'teal' });
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });
  return (
    <Paper withBorder p="md" radius="lg" data-testid="recurring-setting">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Title order={5}>Автопродление</Title>
          <Switch
            size="sm"
            label={status.recurringEnabled ? 'Предлагается' : 'Выключено'}
            checked={status.recurringEnabled}
            disabled={mutation.isPending}
            onChange={(event) => mutation.mutate(event.currentTarget.checked)}
          />
        </Group>
        <Text size="xs" c="var(--text-muted)">
          У тарифов на фиксированное число дней при оплате появляется галочка «Я согласен на автоматические списания…» (по умолчанию снята) с ценой, датой
          следующего списания и порядком повторов. Включайте, когда Robokassa подключила магазину автоплатежи. Уже
          подключённые автопродления выключатель не трогает — их список и отключение в «Продажи → Автопродления».
        </Text>
      </Stack>
    </Paper>
  );
}

/** Что выдаёт товар — подпись на карточке. */
function grantLabel(product: AdminProductRow): string {
  if (product.grantType === 'plan') return `Тариф ${SUBSCRIPTION_PLAN_LABELS[product.plan!]}`;
  if (product.grantType === 'content_access' && product.contentKind) return SELLABLE_CONTENT_LABELS[product.contentKind];
  return 'Владение игрой';
}

/** Полная платёжная ссылка товара: её копируют на сайт, в Telegram, в письма. */
const buyLink = (code: string) => `${window.location.origin}${buyPath(code)}`;

/**
 * Карточка товара: что продаётся, цена и платёжная ссылка видны сразу, а
 * поля правки — только по кнопке «Изменить», чтобы список читался.
 */
function ProductRow({ product }: { product: AdminProductRow }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(product.title);
  const [description, setDescription] = useState(product.description ?? '');
  const [days, setDays] = useState<number>(product.days ?? 30);
  const [years, setYears] = useState<number>(product.years ?? 2);
  const [price, setPrice] = useState<number>((product.priceKopecks ?? 0) / 100);
  const [grace, setGrace] = useState<number>(DEFAULT_PRICE_GRACE_DAYS);
  const done = (message: string) => (result: { items: AdminProductRow[] }) => {
    queryClient.setQueryData(ADMIN_PRODUCTS_KEY, result);
    notifications.show({ message, color: 'teal' });
  };
  const onError = (error: unknown) => notifications.show({ color: 'red', message: toUserMessage(error) });
  const save = useMutation({
    mutationFn: (active: boolean) =>
      updateProduct(product.id, {
        title,
        description,
        active,
        days: product.grantType === 'plan' && !product.years ? days : null,
        years: product.years ? years : null,
      }),
    onSuccess: done('Товар сохранён'),
    onError,
  });
  const reprice = useMutation({ mutationFn: () => changeProductPrice(product.id, { priceRub: price, graceDays: grace }), onSuccess: done('Цена изменена'), onError });
  const close = () => {
    setTitle(product.title);
    setDescription(product.description ?? '');
    setDays(product.days ?? 30);
    setYears(product.years ?? 2);
    setPrice((product.priceKopecks ?? 0) / 100);
    setGrace(DEFAULT_PRICE_GRACE_DAYS);
    setEditing(false);
  };
  const changed = title !== product.title || description !== (product.description ?? '') ||
    (product.grantType === 'plan' && (product.years ? years !== product.years : days !== product.days));

  return (
    <Paper withBorder p="sm" radius="md" data-testid={`product-${product.code}`} style={{ opacity: product.active ? 1 : 0.75 }}>
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Group gap="xs">
              <Text fw={600}>{product.title}</Text>
              <Badge variant="light" color={product.grantType === 'plan' ? 'gold' : 'teal'}>{grantLabel(product)}</Badge>
              {product.years ? <Badge variant="light" color="teal">Спецпредложение</Badge> : null}
            </Group>
            <Text size="xs" c="var(--text-muted)">
              Код <Text span ff="monospace" size="xs">{product.code}</Text>
              {product.grantType === 'content_access' ? ` · открывает навсегда: «${product.contentTitle ?? 'материал удалён'}»` : ''}
            </Text>
          </Stack>
          <Switch size="xs" label={product.active ? 'Продаётся' : 'Выключен'} checked={product.active} disabled={save.isPending} onChange={(event) => save.mutate(event.currentTarget.checked)} />
        </Group>
        <Text size="sm">
          <b>{product.priceKopecks === null ? 'Цена не задана' : formatRubles(product.priceKopecks)}</b>
          {product.grantType === 'plan' ? ` · ${formatPlanDuration(product.days, product.years)}` : ' · бессрочно'}
          {product.priceSince ? <Text span size="xs" c="var(--text-muted)">{` · цена с ${dateTime(product.priceSince)}`}</Text> : null}
        </Text>
        {product.gracePrices.map((old) => (
          <Text key={old.validUntil} size="xs" c="var(--text-muted)">
            Также принимается {formatRubles(old.priceKopecks)} — до {dateTime(old.validUntil)}
          </Text>
        ))}
        {product.description && !editing ? (
          <Text size="xs" c="var(--text-muted)" lineClamp={2} style={{ whiteSpace: 'pre-line' }}>{product.description}</Text>
        ) : null}
        {product.active ? (
          <Group gap="xs" align="flex-end" wrap="nowrap">
            <TextInput size="xs" label="Платёжная ссылка" value={buyLink(product.code)} readOnly style={{ flex: 1, maxWidth: 460 }} onFocus={(event) => event.currentTarget.select()} />
            <CopyButton value={buyLink(product.code)}>
              {({ copied, copy }) => (
                <Button size="xs" variant="light" color={copied ? 'teal' : undefined} leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />} onClick={copy}>
                  {copied ? 'Скопировано' : 'Скопировать'}
                </Button>
              )}
            </CopyButton>
          </Group>
        ) : null}
        {editing ? (
          <Stack gap="xs" pt="xs" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <Group align="flex-end" gap="xs" wrap="wrap">
              <TextInput size="xs" label="Название" description="Оно же — в чеке" value={title} onChange={(event) => setTitle(event.currentTarget.value)} style={{ minWidth: 280 }} />
              {product.grantType === 'plan' ? (
                product.years ? (
                  <NumberInput size="xs" label="Календарных лет" w={150} min={1} max={10} allowDecimal={false} value={years} onChange={(value) => setYears(Number(value) || 0)} />
                ) : (
                  <NumberInput size="xs" label="Дней" w={90} min={1} max={3650} value={days} onChange={(value) => setDays(Number(value) || 0)} />
                )
              ) : null}
            </Group>
            {product.years ? <Text size="xs" c="var(--text-muted)">Доступ «{SUBSCRIPTION_PLAN_LABELS[product.plan!]}» на указанный срок. При действующем таком же тарифе срок добавляется к дате окончания.</Text> : null}
            <Textarea
              size="xs"
              label="Описание — что входит"
              description="Видно покупателю на карточке оплаты. Можно оставить пустым."
              autosize
              minRows={2}
              maxRows={6}
              maxLength={1000}
              value={description}
              onChange={(event) => setDescription(event.currentTarget.value)}
            />
            <Group gap="xs">
              <Button size="xs" disabled={!changed} loading={save.isPending} onClick={() => save.mutate(product.active)}>Сохранить</Button>
              <Button size="xs" variant="subtle" color="gray" onClick={close}>Закрыть</Button>
            </Group>
            <Text size="xs" fw={600} mt="xs">Цена</Text>
            <Text size="xs" c="var(--text-muted)">
              Новая цена действует сразу. Прежняя принимается ещё несколько дней — для счетов, выставленных до смены.
            </Text>
            <Group align="flex-end" gap="xs" wrap="wrap">
              <NumberInput size="xs" label="Новая цена, ₽" w={130} min={1} value={price} onChange={(value) => setPrice(Number(value) || 0)} />
              <NumberInput size="xs" label="Прежняя принимается, дней" w={190} min={0} max={60} value={grace} onChange={(value) => setGrace(Number(value) || 0)} />
              <Button size="xs" variant="light" disabled={price <= 0 || price * 100 === product.priceKopecks} loading={reprice.isPending} onClick={() => reprice.mutate()}>Сменить цену</Button>
            </Group>
          </Stack>
        ) : (
          <Group>
            <Button size="xs" variant="subtle" leftSection={<IconPencil size={14} />} onClick={() => setEditing(true)}>Изменить</Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

type Selling = 'plan' | 'content_access' | 'game_ownership';

/**
 * Новый товар: тариф на срок, отдельный материал (обучение, МАК-сессия) или
 * владение игрой. Код придумывает сервер; название для материала и игры
 * подставляется из выбранного, его можно поправить.
 */
function NewProductForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const content = useQuery({ queryKey: ADMIN_CONTENT_KEY, queryFn: fetchAdminContent });
  const items = content.data?.items ?? [];
  const games = items.filter((item) => item.kind === 'game' && item.variant !== 'demo');
  const [selling, setSelling] = useState<Selling>('content_access');
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [plan, setPlan] = useState<SubscriptionPlan>('light');
  const [days, setDays] = useState(30);
  const [durationUnit, setDurationUnit] = useState<'days' | 'years'>('days');
  const [years, setYears] = useState(2);
  const [gameId, setGameId] = useState<string | null>(null);
  const [contentKind, setContentKind] = useState<SellableContentKind>('training_material');
  const [contentId, setContentId] = useState<string | null>(null);
  const [price, setPrice] = useState(0);
  const materials = items.filter((item) => item.kind === contentKind);

  /** Название по умолчанию — из выбранного материала или игры, пока его не правили руками. */
  const suggest = (picked: string | undefined) => {
    if (!titleTouched) setTitle(picked ?? '');
  };
  const reset = () => {
    setTitle('');
    setTitleTouched(false);
    setDescription('');
    setContentId(null);
    setGameId(null);
    setPrice(0);
  };
  const mutation = useMutation({
    mutationFn: () =>
      createProduct({
        title: title.trim(),
        description,
        grantType: selling,
        plan: selling === 'plan' ? plan : null,
        days: selling === 'plan' && durationUnit === 'days' ? days : null,
        years: selling === 'plan' && durationUnit === 'years' ? years : null,
        gameId: selling === 'game_ownership' ? gameId : null,
        contentKind: selling === 'content_access' ? contentKind : null,
        contentId: selling === 'content_access' ? contentId : null,
        priceRub: price,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(ADMIN_PRODUCTS_KEY, result);
      reset();
      onClose();
      notifications.show({ message: 'Товар добавлен — его платёжная ссылка в списке', color: 'teal' });
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });
  const picked = selling === 'plan' || (selling === 'game_ownership' ? gameId !== null : contentId !== null);

  return (
    <Paper withBorder p="md" radius="md" data-testid="new-product">
      <Stack gap="sm">
        <Title order={5}>Новый товар</Title>
        <SegmentedControl
          size="xs"
          w={360}
          value={selling}
          onChange={(value) => { setSelling(value as Selling); setTitleTouched(false); setTitle(''); }}
          data={[
            { value: 'content_access', label: 'Материал' },
            { value: 'plan', label: 'Тариф' },
            { value: 'game_ownership', label: 'Игра' },
          ]}
        />
        <Text size="xs" c="var(--text-muted)">
          {selling === 'content_access'
            ? 'Бессрочный доступ к одному обучению или МАК-сессии — тем, у кого нет тарифа, который его открывает.'
            : selling === 'plan'
              ? 'Тариф Лайт или Про на срок. Повторная покупка продлевает тариф.'
              : 'Бессрочное владение полной игрой: её можно вести без тарифа.'}
        </Text>
        <Group gap="xs" align="flex-end" wrap="wrap">
          {selling === 'content_access' ? (
            <>
              <Select
                size="xs"
                label="Что продаём"
                w={160}
                data={SELLABLE_CONTENT_KINDS.map((kind) => ({ value: kind, label: SELLABLE_CONTENT_LABELS[kind] }))}
                value={contentKind}
                onChange={(value) => { setContentKind((value as SellableContentKind) ?? 'training_material'); setContentId(null); suggest(undefined); }}
              />
              <Select
                size="xs"
                label={contentKind === 'spread' ? 'МАК-сессия' : 'Обучение'}
                placeholder="Выберите"
                searchable
                w={300}
                nothingFoundMessage="Ничего не найдено"
                data={materials.map((item) => ({ value: item.id, label: item.status === 'published' ? item.title : `${item.title} (не опубликован)` }))}
                value={contentId}
                onChange={(value) => { setContentId(value); suggest(materials.find((item) => item.id === value)?.title); }}
              />
            </>
          ) : selling === 'plan' ? (
            <>
              <Select size="xs" label="Тариф" w={100} data={[{ value: 'light', label: 'Лайт' }, { value: 'pro', label: 'Про' }]} value={plan} onChange={(value) => setPlan((value as SubscriptionPlan) ?? 'light')} />
              <Select size="xs" label="Единица срока" w={160} data={[{ value: 'days', label: 'Дни' }, { value: 'years', label: 'Календарные годы' }]} value={durationUnit} onChange={(value) => setDurationUnit(value === 'years' ? 'years' : 'days')} />
              {durationUnit === 'years' ? (
                <NumberInput size="xs" label="Лет" w={90} min={1} max={10} allowDecimal={false} value={years} onChange={(value) => setYears(Number(value) || 0)} />
              ) : (
                <NumberInput size="xs" label="Дней" w={90} min={1} max={3650} allowDecimal={false} value={days} onChange={(value) => setDays(Number(value) || 0)} />
              )}
            </>
          ) : (
            <Select
              size="xs"
              label="Игра"
              placeholder="Выберите"
              w={260}
              data={games.map((game) => ({ value: game.id, label: game.title }))}
              value={gameId}
              onChange={(value) => { setGameId(value); suggest(games.find((game) => game.id === value)?.title); }}
            />
          )}
        </Group>
        <Group gap="xs" align="flex-end" wrap="wrap">
          <TextInput
            size="xs"
            label="Название для покупателя"
            description="Оно же — в чеке"
            placeholder={selling === 'plan' ? 'Тариф Про, 90 дней' : 'Например, Работа с метафорическими картами'}
            value={title}
            onChange={(event) => { setTitle(event.currentTarget.value); setTitleTouched(true); }}
            style={{ minWidth: 300 }}
          />
          <NumberInput size="xs" label="Цена, ₽" w={110} min={1} value={price} onChange={(value) => setPrice(Number(value) || 0)} />
        </Group>
        <Textarea
          size="xs"
          label="Описание — что входит"
          description="Необязательно. Видно покупателю на карточке оплаты."
          autosize
          minRows={2}
          maxRows={6}
          maxLength={1000}
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
        />
        <Group>
          <Button size="xs" disabled={!picked || title.trim().length < 2 || price <= 0} loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Добавить товар
          </Button>
          <Button size="xs" variant="subtle" color="gray" onClick={onClose}>Отмена</Button>
        </Group>
      </Stack>
    </Paper>
  );
}
